<template>
  <div class="approval-period">
    <!-- TKP-105 / md. 46–56 periyodik güncelleme; TKP-159 / tunesoft2 s.2, s.10: "Devam Ediyor" / "Tamamlandı" sekmeleri,
         sağ üstte "Yeni Güncelleme Başlat (+)" → ad + koşum başına e-posta anahtarı; tamamlananlar salt okunur görüntülenir. -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <!-- TKP-200 m.5 / TKP-209: başlığın altında Ayarlar yönlendirmesi ve dönem satırı her zaman; değer dolu ise "her N ayda bir", boşsa boş -->
      <!-- TKP-233 m.142: Ayarlar yönlendirmesi ayrı satır değil, dönem alanında bilgi (i) ipucu -->
      <div data-test="period-months">
        {{ $t("approval.period.months-label") }}
        <i class="bx bx-info-circle text-info hand" data-test="period-settings-hint" v-b-tooltip.hover :title="$t('approval.period.settings-hint')"></i>:
        <strong v-if="months" data-test="period-months-value">{{ $t("approval.period.every", { months }) }}</strong>
      </div>
      <b-button v-if="canStart" size="sm" variant="primary" :disabled="isBusy" data-test="period-start" @click="openStart">
        <i class="bx bx-plus"></i> {{ $t("approval.period.start-new") }}
      </b-button>
      <!-- TKP-175 md.42: devam eden koşum varken yeni koşum başlatılamaz -->
      <small v-else-if="approver && runs.length" class="text-warning" data-test="period-in-progress">{{ $t("approval.period.in-progress") }}</small>
    </div>
    <!-- TKP-100 / matris E: birim/süreç yöneticisi yalnız kendi kapsamını görür -->
    <small v-if="data.scoped" class="text-muted d-block mb-2" data-test="period-scoped">{{ $t("approval.period.scoped") }}</small>

    <!-- TKP-233 m.140: sekmeler Görevler › Görevler görünümünde -->
    <ul class="nav nav-tab-menu mb-3">
      <li class="nav-item"><a href="javascript:;" class="nav-link" :class="{ active: tab === 'open' }" data-test="tab-open" @click="tab = 'open'">{{ $t("approval.period.tab-open") }} <b-badge variant="light">{{ runs.length }}</b-badge></a></li>
      <li class="nav-item"><a href="javascript:;" class="nav-link" :class="{ active: tab === 'closed' }" data-test="tab-closed" @click="showClosed">{{ $t("approval.period.tab-closed") }}</a></li>
    </ul>

    <!-- Devam Ediyor: açık koşumlar (TKP-135: birim periyoduyla birden çok olabilir), her biri kendi birim tablosuyla -->
    <div v-if="tab === 'open'">
      <div v-if="runs.length === 0" class="text-muted small" data-test="period-empty">{{ $t("approval.period.empty-open") }}</div>
      <b-card v-for="run in runs" :key="run._id" class="mb-3" data-test="period-run">
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <!-- TKP-233 m.146: her bilgi etiketiyle — "Açık" durum rozeti "Durum:" altında, e-posta "E-posta Bildirimi:" -->
            <div>
              <!-- TKP-236 n.6 (karar 2026-11-01): koşum adı ("2026 Bugün") bilgi kartında gösterilmez; Tamamlandı tablosunda ad sütunu kalır -->
              <div class="run-meta text-muted">
                <span class="mr-3">{{ $t("approval.period.run-starter") }}: <b>{{ run.startedByName || "—" }}</b></span>
                <span class="mr-3">{{ $t("approval.period.run-started") }}: <b>{{ run.startedAt | date }}</b></span>
                <span class="mr-3">{{ $t("approval.period.state") }}: <b-badge variant="warning" data-test="period-status">{{ $t("approval.period.status.OPEN") }}</b-badge></span>
                <span :data-test="run.notifyByEmail === false ? 'run-email-off' : 'run-email-on'">{{ $t("approval.period.email") }}: <b>{{ $t(run.notifyByEmail === false ? "approval.period.email-state.off" : "approval.period.email-state.on") }}</b></span>
              </div>
            </div>
            <!-- TKP-160 / tunesoft2 s.9: tüm birimler işaretlenince "Tamamlandı" (onay yetkilisi) -->
            <b-button v-if="canComplete(run)" size="sm" variant="success" :disabled="isBusy" data-test="complete-run" @click="completeRun(run)">{{ $t("approval.period.complete") }}</b-button>
          </div>
          <!-- TKP-160: Tamamlanma Oranı ve birim sayaçları; TKP-233 m.144: kalın, ortalanmış sayaç kutuları -->
          <div class="run-counters d-flex flex-wrap justify-content-center text-center mt-3" data-test="run-counters">
            <div class="run-counter"><div class="run-counter-value" data-test="run-ratio">{{ summaryOf(run).closed }}/{{ summaryOf(run).total }}</div><div class="run-counter-label">{{ $t("approval.period.ratio") }}</div></div>
            <div class="run-counter"><div class="run-counter-value" data-test="run-open">{{ summaryOf(run).open }}</div><div class="run-counter-label">{{ $t("approval.period.count-open") }}</div></div>
            <div class="run-counter"><div class="run-counter-value" data-test="run-closed">{{ summaryOf(run).closed }}</div><div class="run-counter-label">{{ $t("approval.period.count-closed") }}</div></div>
            <div class="run-counter"><div class="run-counter-value" data-test="run-total">{{ summaryOf(run).total }}</div><div class="run-counter-label">{{ $t("approval.period.count-total") }}</div></div>
          </div>
        </template>
        <period-departments-table :period="run" :busy="isBusy" :approver="approver" :admin-department-ids="adminDepartmentIds" :admin-process-ids="adminProcessIds" @remind="remind" @no-change="noChange" @updated="markUpdated" @process-no-change="processNoChange" @process-updated="processUpdated" @reopen="reopen" @process-reopen="processReopen" />
      </b-card>
    </div>

    <!-- Tamamlandı: kapanmış koşumlar — Adı, Sorumlu, Başlangıç, Bitiş, Durumu; görüntüleme salt okunur -->
    <div v-else>
      <div v-if="history.docs.length === 0" class="text-muted small" data-test="history-empty">{{ $t("approval.period.empty-closed") }}</div>
      <table v-else class="table table-sm" data-test="history-table">
        <thead>
          <tr>
            <th>{{ $t("approval.period.run-name") }}</th>
            <th>{{ $t("approval.period.run-starter") }}</th>
            <th>{{ $t("approval.period.run-started") }}</th>
            <th>{{ $t("approval.period.run-closed") }}</th>
            <th>{{ $t("approval.period.run-state") }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="run in history.docs" :key="run._id" data-test="history-row">
            <td>{{ run.name }}</td>
            <td>{{ run.startedByName || "—" }}</td>
            <td><small>{{ run.startedAt | date(true) }}</small></td>
            <td><small>{{ run.closedAt | date(true) }}</small></td>
            <td><b-badge variant="secondary">{{ $t("approval.period.status.CLOSED") }}</b-badge></td>
            <td class="text-right"><a href="javascript:;" data-test="history-view" v-b-tooltip.hover :title="$t('approval.period.view')" @click="view(run)"><i class="bx bx-search-alt"></i></a></td>
          </tr>
        </tbody>
      </table>
      <div v-if="history.totalDocs > history.limit" class="text-right">
        <b-button size="sm" variant="link" :disabled="history.page <= 1" data-test="history-prev" @click="loadHistory(history.page - 1)">‹</b-button>
        <small>{{ history.page }} / {{ Math.ceil(history.totalDocs / history.limit) }}</small>
        <b-button size="sm" variant="link" :disabled="history.page * history.limit >= history.totalDocs" data-test="history-next" @click="loadHistory(history.page + 1)">›</b-button>
      </div>
    </div>

    <!-- Yeni Güncelleme Başlat -->
    <b-modal v-model="startDialog.show" :title="$t('approval.period.start-title')" :ok-title="$t('approval.period.start')" :cancel-title="$t('common.cancel')" :ok-disabled="isBusy" data-test="start-modal" @ok="start">
      <b-form-group :label="$t('approval.period.start-name')" :description="$t('approval.period.start-name-hint', { name: '{Organizasyon}_PG_GGAAYY' })">
        <b-form-input v-model="startDialog.name" data-test="start-name" />
      </b-form-group>
      <b-form-checkbox v-model="startDialog.notifyByEmail" switch data-test="start-email">{{ $t("approval.period.start-email") }}</b-form-checkbox>
    </b-modal>

    <!-- Tamamlanmış koşum içeriği (salt okunur) -->
    <b-modal v-model="detail.show" :title="detail.run ? detail.run.name : ''" size="lg" ok-only :ok-title="$t('common.close')" data-test="history-detail">
      <template v-if="detail.run">
        <period-departments-table :period="detail.run" readonly />
      </template>
    </b-modal>
  </div>
</template>

<script>
import PeriodDepartmentsTable from "./period-departments-table";
import { canCompleteRun, periodSummary } from "./approval";

export default {
  name: "approval-period",
  components: { PeriodDepartmentsTable },
  data() {
    return { tab: "open", history: { docs: [], totalDocs: 0, page: 1, limit: 20 }, startDialog: { show: false, name: "", notifyByEmail: true }, detail: { show: false, run: null } };
  },
  computed: {
    data() {
      return this.$store.getters["approval/period"] || {};
    },
    /** Devam eden koşumlar (TKP-159); eski yanıt biçiminde `runs` yoksa açık `period` tek koşum sayılır. */
    runs() {
      if (Array.isArray(this.data.runs)) return this.data.runs;
      return this.data.period && this.data.period.status === "OPEN" ? [this.data.period] : [];
    },
    period() {
      return this.data.period || null;
    },
    months() {
      return this.data.months || 0;
    },
    canStart() {
      return !!this.data.canStart;
    },
    isBusy() {
      return this.$store.getters["approval/isBusy"];
    },
    approver() {
      const status = this.$store.getters["approval/status"];
      return !!(status && status.approver);
    },
    adminDepartmentIds() {
      return (this.currentAccountDepartments || []).map((d) => String(d._id || d));
    },
    adminProcessIds() {
      return (this.currentAccountProcesses || []).map((p) => String(p._id || p));
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    summaryOf: periodSummary,
    canComplete(run) {
      return canCompleteRun(run, this.approver);
    },
    async completeRun(run) {
      try {
        const { data } = await this.$store.dispatch("approval/completePeriod", run._id);
        // TKP-175 md.44: güncel envanter Dosya Yöneticisi › Envanter dosyalarına koşum adıyla kaydedildi
        this.$toast.success(this.$t(data && data.versionFile ? "approval.period.completed-file" : "approval.period.completed", { file: data && data.versionFile ? data.versionFile.name : "" }));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async markUpdated(entry) {
      try {
        await this.$store.dispatch("approval/periodUpdated", entry.department);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async load() {
      try {
        // TKP-160: kendi sayfasında (TKP-153) onay durumu yüklü olmayabilir — onay yetkilisi bayrağı tuşları belirler
        if (!this.$store.getters["approval/status"]) await this.$store.dispatch("approval/status");
        await this.$store.dispatch("approval/fetchPeriod");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async showClosed() {
      this.tab = "closed";
      await this.loadHistory(1);
    },
    async loadHistory(page) {
      try {
        const { data } = await this.$store.dispatch("approval/fetchPeriodHistory", { page, limit: this.history.limit });
        this.history = { docs: data.docs || [], totalDocs: data.totalDocs || 0, page: data.page || page, limit: data.limit || this.history.limit };
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async view(run) {
      try {
        const { data } = await this.$store.dispatch("approval/fetchPeriodDetail", run._id);
        this.detail = { show: true, run: data };
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    openStart() {
      this.startDialog = { show: true, name: "", notifyByEmail: true }; // karar 2026-09-29: e-posta varsayılan açık
    },
    async start() {
      try {
        await this.$store.dispatch("approval/startPeriod", { name: this.startDialog.name.trim(), notifyByEmail: this.startDialog.notifyByEmail });
        this.$toast.success(this.$t("approval.period.started"));
        this.tab = "open";
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async processNoChange(proc) {
      try {
        await this.$store.dispatch("approval/periodProcessNoChange", proc.process);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    // TKP-179 md.63
    async processUpdated(proc) {
      try {
        await this.$store.dispatch("approval/periodProcessUpdated", proc.process);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async reopen(entry) {
      if (!(await this.confirm(this.$t("approval.period.reopen-confirm")))) return;
      try {
        await this.$store.dispatch("approval/periodReopen", entry.department);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async processReopen(proc) {
      if (!(await this.confirm(this.$t("approval.period.reopen-confirm")))) return;
      try {
        await this.$store.dispatch("approval/periodProcessReopen", proc.process);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async remind(entry) {
      try {
        await this.$store.dispatch("approval/periodRemind", entry.department);
        this.$toast.success(this.$t("approval.period.reminded"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async noChange(entry) {
      try {
        await this.$store.dispatch("approval/periodNoChange", entry.department);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>

<style lang="scss">
/* TKP-233 m.143: sayfa metinleri Varlıklar › envanter özellikleri paneliyle aynı boyutta (taban yazı 0.8125rem; <small> küçültmesi yok) */
.approval-period {
  font-size: 0.8125rem;
  small,
  .small,
  .table {
    font-size: 0.8125rem;
  }
  .run-name {
    font-size: 1rem;
  }
  .run-meta {
    margin-top: 0.25rem;
  }
  /* TKP-233 m.144 */
  .run-counter {
    min-width: 11rem;
    padding: 0.5rem 1.5rem;
    border-left: 1px solid #e9ebec;
    &:first-child {
      border-left: 0;
    }
  }
  .run-counter-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #071437;
  }
  .run-counter-label {
    font-weight: 600;
    color: #74788d;
  }
}
</style>
